import type { Row as TRow, RowData } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { Checkbox } from '../../../Inputs';
import { BodyCell } from '../../components/BodyCell';
import { TableRow } from '../../components/TableRow';
import { useDataProvider } from '../../DataProviderContext';
import { getTableColumnDef } from '../../internal/getTableColumnDef';

interface RowProps<TData extends RowData> {
  row: TRow<TData>;
  isHighlight?: boolean;
  isClickable?: boolean;
  onClick?: (row: TRow<TData>) => void;
}

export const Row = <TData extends RowData>({
  row,
  isClickable = false,
  isHighlight = false,
  onClick,
}: RowProps<TData>) => {
  const { table, isRowIndeterminate } = useDataProvider();
  const enableRowSelection = Boolean(table?.options.enableRowSelection);
  const isIndeterminate = isRowIndeterminate?.(row) ?? false;

  return (
    <TableRow
      isClickable={isClickable}
      isHighlight={isHighlight}
      onClick={() => onClick?.(row)}
    >
      {enableRowSelection && (
        <BodyCell padding="checkbox">
          <Checkbox
            name="select-row"
            checked={row.getIsSelected()}
            indeterminate={isIndeterminate}
            onChange={() => row.toggleSelected()}
            onClick={(e) => e.stopPropagation()}
          />
        </BodyCell>
      )}
      {row.getVisibleCells().map((cell) => {
        const columnDef = getTableColumnDef(cell.column);

        return (
          <BodyCell key={cell.id} padding={columnDef.cellPadding}>
            {cell.getIsPlaceholder()
              ? null
              : flexRender(cell.column.columnDef.cell, cell.getContext())}
          </BodyCell>
        );
      })}
    </TableRow>
  );
};
